// src/components/shared/SelectAllCheckbox.js
// Checkbox tri-state para header de listas com seleção em lote.
//
// Props:
//   totalCount      (number)  — quantos itens visíveis no momento
//   selectedCount   (number)  — quantos desses estão selecionados
//   onSelectAll     (function)— chamado quando clica em estado vazio OU parcial
//   onDeselectAll   (function)— chamado quando clica em estado "todos"
//   className       (string)  — opcional, classes extras no wrapper
//   noun            (string)  — opcional, modificador inserido após "todos" no empty state
//                                (ex: noun="visíveis" → "Selecionar todos visíveis (N)")
//
// Estados visuais:
//   - selectedCount === 0      → unchecked + "Selecionar todos (N)"
//   - 0 < selectedCount < N    → indeterminate + "X de N selecionados"
//   - selectedCount === total  → checked + "Todos selecionados (N)"
//
// Clique:
//   - unchecked/indeterminate → onSelectAll()
//   - checked                 → onDeselectAll()
//
// Edge case: totalCount=0 → checkbox desabilitado, sem label.

import React, { useEffect, useRef } from 'react';

export default function SelectAllCheckbox({
  totalCount = 0,
  selectedCount = 0,
  onSelectAll,
  onDeselectAll,
  className = '',
  noun = '',
}) {
  const ref = useRef(null);

  const allSelected = totalCount > 0 && selectedCount >= totalCount;
  const partial = selectedCount > 0 && selectedCount < totalCount;

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = partial;
  }, [partial]);

  const disabled = totalCount === 0;
  const nounSpace = noun ? ` ${noun}` : '';

  const label = disabled
    ? ''
    : allSelected
      ? `Todos${nounSpace} selecionados (${totalCount})`
      : partial
        ? `${selectedCount} de ${totalCount}${nounSpace} selecionados`
        : `Selecionar todos${nounSpace} (${totalCount})`;

  const handleChange = () => {
    if (disabled) return;
    if (allSelected) {
      if (typeof onDeselectAll === 'function') onDeselectAll();
    } else {
      if (typeof onSelectAll === 'function') onSelectAll();
    }
  };

  return (
    <label
      className={`inline-flex items-center gap-2 text-xs select-none ${
        disabled ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700 cursor-pointer'
      } ${className}`}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={allSelected}
        disabled={disabled}
        onChange={handleChange}
        className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-1 disabled:opacity-50"
      />
      {label && <span>{label}</span>}
    </label>
  );
}
